import Meta from "@/components/meta";
import Navbar from "@/components/navbar";
import Link from "next/link";
import Image from "next/image";
import Footer from "@/components/footer";
import { useI18n } from "next-localization";

import imgHero from "@/img/product/hero.webp";
import imgProductDrilldown from "@/img/product/drilldown.png";
import imgProductReductions from "@/img/product/reductions.png";

import Testimonial from "@/components/product/testimonial";
import imgTestimonialShopify from "@/img/testimonials/shopify.svg";
import imgTestimonialBlock from "@/img/testimonials/block.png";

import sections from "@/components/product/sections";
import Section from "@/components/product/section";
import Screenshot from "@/components/product/screenshot";
import { ModuleColumn } from "@/components/module";
import CleanEnergyModule from "@/components/product/modules/clean_energy";
import CSMModule from "@/components/product/modules/csm";
import MarketplaceModule from "@/components/product/modules/marketplace";
import StandardsLogos from "@/components/home/standards_logos";
import ClimateScienceVisual from "@/components/home/climate_science_visual";
import PortfolioBuilderModule from "@/components/product/modules/portfolio_builder";
import SuppliersModule from "@/components/product/modules/suppliers";
import RemovalPrimerModule from "@/components/product/modules/removal_primer";
import PresentationGallery from "@/components/product/modules/presentation";
import Demobar from "@/components/demobar";

function TOC() {
  return (
    <div className="toc-container">
      <section className="container toc">
        {Object.entries(sections).map(([key, section]) => {
          const Icon = section.icon;
          return (
            <a
              href={`#${key}`}
              key={key}
              style={{
                // @ts-expect-error custom property
                "--colors-icon": section.colorPrimary,
              }}
            >
              <Icon />
              {section.title}
            </a>
          );
        })}
      </section>
      <style jsx>{`
        .toc {
          width: 100%;
          display: flex;
          overflow: hidden;
          padding: 12px 28px 12px 48px;
          justify-content: center;
          gap: 48px;
        }
        .toc-container {
          width: 100%;
          position: sticky;
          position: -webkit-sticky;
          top: 60px;
          background-color: #fff;
          box-shadow: 0 1px 0 rgba(0, 0, 0, 0.05), 0 -1px 0 rgba(0, 0, 0, 0.05);
          z-index: 9;
        }
        .toc a {
          display: flex;
          align-items: center;
          font-size: 14px;
          text-decoration: none;
          color: inherit;
        }
        .toc :global(svg) {
          fill: var(--colors-icon);
          width: 24px;
          height: 24px;
          margin-right: 8px;
        }
        @media (max-width: 520px) {
          .toc-container {
            top: unset;
            bottom: 0;
            position: fixed;
            box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.05);
          }
          .toc {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 16px;
            padding: 12px 24px 8px;
          }
          strong,
          strong + span {
            display: none;
          }
          .toc a {
            flex-direction: column;
            gap: 2px;
          }
        }
        @supports (backdrop-filter: saturate(180%) blur(8px)) or
          (-webkit-backdrop-filter: saturate(180%) blur(8px)) {
          .toc-container {
            background-color: rgba(255, 255, 255, 0.875);
            -webkit-backdrop-filter: saturate(180%) blur(8px);
            backdrop-filter: saturate(180%) blur(8px);
          }
        }
      `}</style>
    </div>
  );
}

function Header() {
  const { t } = useI18n();
  return (
    <header>
      <div>
        <h1>
          Everything you need to run a{" "}
          <span style={{ whiteSpace: "nowrap" }}>world-class</span> climate{" "}
          {t("common.program")}.
        </h1>
        <p>
          Make a plan designed for real-world results with our all-in-one
          solution. Trusted by some of the world’s most{" "}
          <Link href="/customers">
            <a>forward-thinking companies</a>
          </Link>
          .
        </p>
        <Link href="/demo">
          <a className="button primary">Request a demo</a>
        </Link>
      </div>
      <style jsx>{`
        header {
          padding: 24px 16px 0;
        }
        h1 {
          margin-top: 0;
          margin-bottom: 24px;
          font-size: 42px;
          line-height: 1.125;
        }
        p {
          font-size: 18px;
          margin-bottom: 24px;
        }
        @media screen and (min-width: 520px) {
          header {
            margin: 0 auto;
            padding: 48px 32px 24px;
            text-align: center;
          }
          h1 {
            letter-spacing: -0.04em;
            font-size: 64px;
            max-width: 28ch;
            margin: 16px auto 24px;
          }
          p {
            max-width: 56ch;
            font-size: 22px;
            margin: 24px auto;
          }
        }
        p a {
          font-weight: bold;
        }
        .button {
          font-size: 20px;
          padding: 8px 24px;
        }
      `}</style>
    </header>
  );
}

function Hero() {
  return (
    <figure>
      <Image
        alt="Screenshot of the Watershed Dashboard"
        src={imgHero}
        layout="responsive"
        placeholder="blur"
        objectPosition="top center"
        objectFit="cover"
        priority
      />
      <style jsx>{`
        figure {
          background-image: linear-gradient(#fff, #f7f7f9);
        }
        @media screen and (max-width: 520px) {
          figure {
            margin-top: -16px;
            position: relative;
            z-index: -1;
          }
          figure :global(img) {
            width: 140vw !important;
            max-width: none !important;
            margin-left: -10vw !important;
          }
        }
        @media screen and (min-width: 820px) {
          figure > :global(span) {
            max-width: 80vw;
            margin: auto !important;
          }
        }
      `}</style>
    </figure>
  );
}

export default function ProductPage() {
  const { t } = useI18n();
  return (
    <>
      <Meta
        title="Product"
        description="Build a world-class climate strategy and a detailed plan to achieve real results. Measure your footprint, reduce and remove emissions, and report on progress, quickly. Trusted by some of the world’s most forward-thinking companies."
      />
      <Navbar />
      <Header />
      <Hero />
      <TOC />
      <Section
        number={1}
        {...sections.measure}
        headline="Count every ton of carbon, quickly."
      >
        <ModuleColumn
          headline="Rigorous carbon accounting, powered by the latest climate science"
          doubleHeight
          graphic={
            <div
              className="padded-box"
              style={{
                margin: "auto",
                lineHeight: 0,
                width: "100%",
              }}
            >
              <ClimateScienceVisual />
            </div>
          }
        >
          <p>
            Watershed’s carbon data engine {t("common.analyzes")} emissions for
            every line item of your business. We measure Scope 1, 2, and 3 of
            the <a href="https://ghgprotocol.org/">Greenhouse Gas Protocol</a>,
            and have developed state-of-the-art methodologies for{" "}
            <Link href="/blog/decoupling-work-from-carbon">remote work</Link>
            {", "}
            <Link href="/blog/carbon-in-the-cloud">cloud computing</Link>,
            cryptocurrencies, food, apparel, and more.
          </p>
          <p>
            We integrate tens of thousands of emissions factors from government
            publications (including DEFRA and EPA), LCA dashboards (like
            Ecoinvent), academic research, and company reports. Tested by the
            most exacting auditors.
          </p>
        </ModuleColumn>
        <SuppliersModule />
        <ModuleColumn headline="Know your footprint in weeks, not months">
          <p>
            Upload raw data on financials, buildings, travel, and more; we take
            care of the rest. Drag-and-drop files in the dashboard, or automate
            data collection with our API. Your footprint auto-refreshes with any
            new data, so you’re never waiting weeks for a new PDF.
          </p>
        </ModuleColumn>
        <ModuleColumn
          graphic={
            <Screenshot
              src={imgProductDrilldown}
              alt="Drilldown"
              caption={`Go deep on your data with a real-time dashboard that enables ${t(
                "common.categorization"
              )}, filtering, and drill-downs by GHGP scope and category, vendor, location, and more.`}
              cropped
            />
          }
          headline="Turn numbers into action"
          doubleWidth
        >
          <p>
            {t("common.Analyze")} your emissions with a real-time dashboard that
            enables line-by-line drill-downs by scope, category, vendor,
            location, and more, with dollar-for-dollar benchmarks against others
            in your industry.
          </p>
        </ModuleColumn>

        <Testimonial
          href="/blog/insights-from-shopify-remote-era-climate-journey"
          logo={imgTestimonialShopify}
          logoAlt="Shopify logo"
          quote={
            <>
              We went from generic{" "}
              <strong>industry averages to a live model</strong>—built on our
              actual data—<strong>in just a few weeks</strong>. And this
              modelling opened up a thousand new routes to drive down emissions
              across our value chain.
            </>
          }
          name="Stacy Kauk"
          credential="Director of Shopify’s Sustainability Fund"
        />
      </Section>
      <Section
        number={2}
        headline="Real results, not empty promises."
        {...sections.reduce}
      >
        <ModuleColumn
          headline="Set goals and take action"
          graphic={
            <Screenshot
              src={imgProductReductions}
              alt="Screenshot of the Watershed Reductions product"
              cropped
            />
          }
          doubleWidth
        >
          <p>
            {t("common.Analyze")} your emissions and eliminate them from every
            corner of your business. Build your detailed plan based on a
            state-of-the-art projection of future emissions. Set ambitious{" "}
            <Link href="/blog/a-standard-for-net-zero">Net Zero</Link> goals in
            line with the rigorous standards of the{" "}
            <Link href="/blog/science-based-targets-a-guide-for-companies">
              Science-Based Targets initiative (SBTi)
            </Link>
            .
          </p>
        </ModuleColumn>
        <CleanEnergyModule />
        <CSMModule />
        <Testimonial
          href="/blog/square-building-a-world-class-climate-program-in-six-months"
          logo={imgTestimonialBlock}
          logoAlt="Block logo"
          quote={
            <>
              Watershed allowed Block to{" "}
              <strong>
                build a powerful and deeply specific climate action{" "}
                {t("common.program")} literally from scratch
              </strong>
              . They move fast and with precision which has been a perfect
              alliance. I consider them our long-term, end-to-end sustainability
              partners.
            </>
          }
          name="Neil Jorgensen"
          credential="Global ESG Lead, Block"
        />
      </Section>
      <Section
        number={3}
        headline="Fund carbon removal that matters."
        {...sections.remove}
      >
        <MarketplaceModule />
        <PortfolioBuilderModule />
        <RemovalPrimerModule />
      </Section>
      <Section
        number={4}
        headline="Carbon reporting as rigorous as your financials."
        {...sections.report}
      >
        <ModuleColumn
          headline="Investor-grade reporting"
          graphic={
            <div
              className="padded-box"
              style={{
                paddingTop: "0 !important",
                margin: "0 auto",
              }}
            >
              <StandardsLogos />
            </div>
          }
        >
          <p>
            Export audit-ready, investor-grade reports, formatted to meet the{" "}
            <a href="https://ghgprotocol.org/">
              gold standard for carbon accounting
            </a>
            , whenever you need them.
          </p>
          <p>
            Watershed reports are consistent with{" "}
            <Link href="/blog/tcfd-standards-what-companies-need-to-know">
              TCFD
            </Link>
            , <a href="https://www.cdp.net/en">CDP</a>, SASB, and GRI global
            standards, as well as country-specific ones such as{" "}
            <Link href="/blog/eu-and-uk-climate-disclosure-programmes-an-overview">
              SECR in the UK
            </Link>
            . Our climate expert team can prepare and submit your CDP report,
            and work hand-in-hand with third party auditors.
          </p>
        </ModuleColumn>
        <ModuleColumn
          headline={`Keep your whole ${t("common.organization")} in the loop`}
          graphic={
            <div
              style={{
                alignSelf: "center",
                justifySelf: "center",
                padding: "8px 12px 24px",
                marginTop: "auto",
                maxWidth: "100%",
              }}
            >
              <PresentationGallery />
              <p
                style={{
                  textAlign: "center",
                  fontSize: 14,
                  marginTop: 8,
                  marginBottom: 0,
                  color: "#6d7384",
                  lineHeight: 1.25,
                }}
              >
                Present your latest footprint data anytime
              </p>
            </div>
          }
        >
          <p>
            Dynamic, real-time presentations keep employees, leaders, and
            stakeholders engaged on your climate {t("common.program")}. Give
            teams the tools and data they need, with {t("common.customizable")},
            granular access permissions.
          </p>
        </ModuleColumn>
      </Section>
      <div className="container">
        <Demobar />
      </div>
      <style jsx global>{`
        :root {
          --module-color: hsl(215, 10%, 95.49%);
          --module-color-hover: hsl(215, 10%, 89.8%);
          --module-color-quote: hsl(215, 10%, 87.06%);
          --module-color-hover-quote: hsl(215, 10%, 77.06%);
          --module-gap: 32px;
          --module-radius: 16px;
        }
        @media (min-width: 820px) {
          .padded-box {
            padding: 36px 40px;
          }
        }
        html {
          scroll-behavior: smooth;
        }
      `}</style>
      <Footer />
    </>
  );
}

export { getStaticProps } from "@/lib/locales";
